import { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Download,
  Filter,
  Users,
  DollarSign,
  TrendingUp,
  UserCheck,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

interface Client {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  status: 'active' | 'inactive' | 'pending';
  assignedIB: string;
  totalDeposit: string;
  tradingVolume: string;
  profitLoss: string;
  lastTrade: string;
  joinedDate: string;
}

const IBClients = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const clients: Client[] = useMemo(
    () => [
      {
        id: '1',
        name: 'John Smith',
        email: 'john.smith@email.com',
        referralCode: 'IB-123456',
        status: 'active',
        assignedIB: 'John Anderson',
        totalDeposit: '$25,000',
        tradingVolume: '$150,000',
        profitLoss: '+$3,250',
        lastTrade: '2 hours ago',
        joinedDate: '2024-01-15',
      },
      {
        id: '2',
        name: 'Sarah Johnson',
        email: 'sarah.j@email.com',
        referralCode: 'IB-234567',
        status: 'active',
        assignedIB: 'John Anderson',
        totalDeposit: '$15,000',
        tradingVolume: '$85,000',
        profitLoss: '+$1,890',
        lastTrade: '5 hours ago',
        joinedDate: '2024-02-20',
      },
      {
        id: '3',
        name: 'Mike Williams',
        email: 'mike.w@email.com',
        referralCode: 'IB-345678',
        status: 'pending',
        assignedIB: 'Michael Chen',
        totalDeposit: '$5,000',
        tradingVolume: '$12,000',
        profitLoss: '-$250',
        lastTrade: '1 day ago',
        joinedDate: '2024-03-10',
      },
      {
        id: '4',
        name: 'Emily Davis',
        email: 'emily.d@email.com',
        referralCode: 'IB-456789',
        status: 'active',
        assignedIB: 'Michael Chen',
        totalDeposit: '$50,000',
        tradingVolume: '$320,000',
        profitLoss: '+$8,750',
        lastTrade: '3 hours ago',
        joinedDate: '2023-12-05',
      },
      {
        id: '5',
        name: 'Robert Brown',
        email: 'robert.brown@email.com',
        referralCode: 'IB-567890',
        status: 'active',
        assignedIB: 'John Anderson',
        totalDeposit: '$32,000',
        tradingVolume: '$180,000',
        profitLoss: '+$4,120',
        lastTrade: '4 hours ago',
        joinedDate: '2024-01-28',
      },
      {
        id: '6',
        name: 'Jennifer Lee',
        email: 'jennifer.lee@email.com',
        referralCode: 'IB-678901',
        status: 'active',
        assignedIB: 'Sarah Mitchell',
        totalDeposit: '$18,000',
        tradingVolume: '$95,000',
        profitLoss: '+$2,340',
        lastTrade: '6 hours ago',
        joinedDate: '2024-02-15',
      },
      {
        id: '7',
        name: 'David Wilson',
        email: 'david.wilson@email.com',
        referralCode: 'IB-789012',
        status: 'inactive',
        assignedIB: 'Lisa Wang',
        totalDeposit: '$8,000',
        tradingVolume: '$25,000',
        profitLoss: '-$1,200',
        lastTrade: '2 weeks ago',
        joinedDate: '2024-03-05',
      },
      {
        id: '8',
        name: 'Amanda Taylor',
        email: 'amanda.taylor@email.com',
        referralCode: 'IB-890123',
        status: 'active',
        assignedIB: 'Michael Chen',
        totalDeposit: '$75,000',
        tradingVolume: '$450,000',
        profitLoss: '+$12,500',
        lastTrade: '1 hour ago',
        joinedDate: '2023-11-20',
      },
    ],
    []
  );

  const filterFields: FilterField[] = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
      ],
    },
    {
      id: 'assignedIB',
      label: 'Assigned IB',
      options: [
        { value: 'all', label: 'All IBs' },
        { value: 'ib1', label: 'IB John' },
        { value: 'ib2', label: 'IB Jane' },
      ],
    },
  ];

  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      if (activeFilters.status && client.status !== activeFilters.status) {
        return false;
      }
      if (activeFilters.assignedIB && activeFilters.assignedIB !== 'all') {
        if (
          activeFilters.assignedIB === 'ib1' &&
          client.assignedIB !== 'John Anderson'
        ) {
          return false;
        }
        if (
          activeFilters.assignedIB === 'ib2' &&
          client.assignedIB !== 'Sarah Mitchell'
        ) {
          return false;
        }
      }
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          client.name.toLowerCase().includes(searchLower) ||
          client.email.toLowerCase().includes(searchLower) ||
          client.referralCode.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [clients, activeFilters, searchValue]);

  const columns: DataTableColumn<Client>[] = [
    {
      id: 'name',
      header: 'Client Name',
      accessor: row => (
        <div>
          <div className="font-medium">{row.name}</div>
          <div className="text-sm text-muted-foreground">{row.email}</div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
      width: '120px',
    },
    {
      id: 'referralCode',
      header: 'Referral Code',
      accessor: row => (
        <code className="px-2 py-1 bg-muted rounded text-sm">
          {row.referralCode}
        </code>
      ),
      sortable: true,
      sortValue: row => row.referralCode,
      width: '150px',
    },
    {
      id: 'assignedIB',
      header: 'Assigned IB',
      accessor: row => row.assignedIB,
      sortable: true,
      sortValue: row => row.assignedIB,
      width: '150px',
    },
    {
      id: 'totalDeposit',
      header: 'Total Deposit',
      accessor: row => row.totalDeposit,
      sortable: true,
      sortValue: row => row.totalDeposit,
      width: '140px',
    },
    {
      id: 'tradingVolume',
      header: 'Trading Volume',
      accessor: row => row.tradingVolume,
      sortable: true,
      sortValue: row => row.tradingVolume,
      width: '150px',
    },
    {
      id: 'profitLoss',
      header: 'Profit/Loss',
      accessor: row => (
        <span
          className={`font-semibold ${
            row.profitLoss.startsWith('+')
              ? 'text-green-600'
              : row.profitLoss.startsWith('-')
                ? 'text-red-600'
                : 'text-gray-600'
          }`}
        >
          {row.profitLoss}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profitLoss,
      width: '130px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={true}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View client:', row.id)}
          onEdit={() => console.log('Edit client:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB Clients"
          description="Manage and monitor client accounts"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Clients"
          value={clients.length.toString()}
          change="+3 from last month"
          icon={Users}
        />
        <StatsCard
          title="Active Clients"
          value={clients.filter(c => c.status === 'active').length.toString()}
          change={`${Math.round((clients.filter(c => c.status === 'active').length / clients.length) * 100)}% active`}
          icon={UserCheck}
        />
        <StatsCard
          title="Total Deposit"
          value="$228,000"
          change="+18.5% from last month"
          icon={DollarSign}
        />
        <StatsCard
          title="Trading Volume"
          value="$1,317,000"
          change="+22.3% from last month"
          icon={TrendingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>My Clients</CardTitle>
              <CardDescription>
                Manage and view all client accounts
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <InlineFilter
            showFilters={showFilters}
            onToggle={() => setShowFilters(!showFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={() => setActiveFilters({})}
            onFiltersApply={() => {}}
            title="Filter Clients"
          />
          <DataTable
            columns={columns}
            data={filteredClients}
            searchValue={searchValue}
            searchPlaceholder="Search clients..."
            onSearchChange={setSearchValue}
            onSearchReset={() => setSearchValue('')}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={() => {}}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IBClients;
